/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/pat/PatPanel" />
import * as React from "react";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { IPatPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/pat/PatPanel.Props";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { createPatRequest } from "Wit/Settings/BoardsExternalIntegration/utils";

export interface IPatPanelPanelState {
    isDirty: boolean;
    token: string;
    errorMessage?: string;
    loadingSpinnerMessage?: string;
}

export class PatPanel extends VssComponent<IPatPanelProps, IPatPanelPanelState> {
    private _textFieldRef = React.createRef<TextField>();

    constructor(props: IPatPanelProps) {
        super(props);
        this.state = {
            isDirty: false,
            errorMessage: undefined,
            token: ""
        };
    }

    public componentDidMount() {
        requestAnimationFrame(() => {
            if (this._textFieldRef.current) {
                this._textFieldRef.current.focus();
            }
        });
    }

    public render() {
        return (
            <BasePanel
                onDismiss={this._onDismiss}
                onDismissErrorMessage={this._onDismissErrorMessage}
                title={Resources.NewConnection_title}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                className="form-panel"
                errorMessage={this.state.errorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
            >
                <div className="form-panel-content">
                    <div className="input-label fontWeightSemiBold">{Resources.PAT}</div>
                    <TextField
                        ariaLabel={Resources.PAT}
                        className="input-field"
                        onChange={this._onChanged}
                        placeholder={Resources.GitHubAuthToken_Placeholder}
                        inputType="password"
                        value={this.state.token}
                        ref={this._textFieldRef}
                    />
                    <div className="input-description">
                        {Resources.Pat_description}
                        <br />
                        {GitHubConnectionConstants.PatScopes}
                    </div>
                </div>
                <Link className="form-panel-footer" target="_blank" href={GitHubConnectionConstants.PersonalAccessTokenLink}>
                    {Resources.Pat_LearnMoreAboutPat}
                </Link>
            </BasePanel>
        );
    }

    private _onDismiss = (): void => {
        if (!this.state.loadingSpinnerMessage) {
            if (!this.state.isDirty) {
                this.props.onDismiss();
            } else {
                showConfirmationDialog(
                    this.context,
                    Resources.ConfirmationClosePanelMessage,
                    Resources.ConfirmationClosePanelActionText,
                    Resources.ConfirmationClosePanelTitle,
                    this.props.onDismiss
                );
            }
        }
    };

    private _onDismissErrorMessage = () => {
        this.setState({ errorMessage: undefined });
    };

    private _renderHeader = () => {
        return <div>{Resources.Pat_subheader}</div>;
    };

    private _renderFooter = (): JSX.Element => {
        return (
            <div className="align-right">
                <Button text={Resources.Cancel} onClick={this.props.onDismiss} />
                <Button primary={true} text={Resources.Connect} onClick={this._onConnectButtonClick} disabled={!this._canConnect()} />
            </div>
        );
    };

    private _onConnectButtonClick = () => {
        this.setState({ loadingSpinnerMessage: Resources.SpinnerConnectingAccountText });
        this._authenticate();
    };

    private _onChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this.setState({
            isDirty: true,
            token: value,
            errorMessage: undefined
        });
    };

    private _canConnect = (): boolean => {
        return !!this.state.token;
    };

    private _authenticate = async () => {
        const { existingServiceConnections, onConnectionCreated } = this.props;
        const { token } = this.state;

        try {
            const serviceEndpoint = await createPatRequest(this.context.pageContext, token, existingServiceConnections);
            onConnectionCreated(serviceEndpoint);
        } catch (error) {
            this.setState({
                loadingSpinnerMessage: undefined,
                errorMessage: error && error.message
            });
        }
    };
}
